import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BookOpen,
  CheckCircle,
  ChevronDown,
  ClipboardList,
  CreditCard,
  GraduationCap,
  LogOut,
  LucideAngularModule,
  Menu,
  MessageSquareHeart,
  X,
} from 'lucide-angular';
import { AuthService } from '../../../common/auth/auth.service';
import { ToastService } from '../../../common/services/toast.service';
import { AmplitudeService } from '../../../common/analytics/amplitude.service';
import { DashboardFeedbackModalComponent } from './dashboard-feedback-modal/dashboard-feedback-modal';
import { PricingModalComponent } from '../../paywall/pricing-modal/pricing-modal';
import { SubscriptionSuccessModalComponent } from '../../paywall/subscription-success-modal/subscription-success-modal';
import { SubscriptionInfoModalComponent } from '../../paywall/subscription-info-modal/subscription-info-modal';
import { DashboardLayoutService } from './services/dashboard-layout.service';
import { DashboardSubscriptionService } from './services/dashboard-subscription.service';
import { DashboardFeedbackService } from './services/dashboard-feedback.service';

type DashboardTab = 'lesson-plans' | 'assignments' | 'grading';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    DashboardFeedbackModalComponent,
    PricingModalComponent,
    SubscriptionSuccessModalComponent,
    SubscriptionInfoModalComponent,
  ],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly destroyRef = inject(DestroyRef);

  // Services
  protected readonly layoutService = inject(DashboardLayoutService);
  protected readonly subscriptionService = inject(DashboardSubscriptionService);
  protected readonly feedbackService = inject(DashboardFeedbackService);

  // Expose service observables
  protected readonly userProfile$ = this.subscriptionService.userProfile$;
  protected readonly isUserMenuOpen$ = this.layoutService.isUserMenuOpen$;
  protected readonly isMobileSidebarOpen$ = this.layoutService.isMobileSidebarOpen$;
  protected readonly isPricingModalOpen$ = this.subscriptionService.isPricingModalOpen$;
  protected readonly isSubscriptionSuccessOpen$ = this.subscriptionService.isSubscriptionSuccessOpen$;
  protected readonly subscribedProductName$ = this.subscriptionService.subscribedProductName$;
  protected readonly isSubscriptionInfoOpen$ = this.subscriptionService.isSubscriptionInfoOpen$;
  protected readonly isCancellingSubscription$ = this.subscriptionService.isCancellingSubscription$;
  protected readonly cancellationSuccess$ = this.subscriptionService.cancellationSuccess$;
  protected readonly isDashboardFeedbackOpen$ = this.feedbackService.isDashboardFeedbackOpen$;
  protected readonly toastState$ = this.toastService.state$;

  protected readonly icons = {
    BookOpen,
    ClipboardList,
    GraduationCap,
    LogOut,
    CheckCircle,
    X,
    MessageSquareHeart,
    Menu,
    ChevronDown,
    CreditCard,
  };

  protected readonly tabs: { id: DashboardTab; label: string; icon: typeof BookOpen; route: string }[] = [
    { id: 'assignments', label: 'Assignments', icon: ClipboardList, route: '/teacher/dashboard/assignments' },
    { id: 'lesson-plans', label: 'Lesson Plans', icon: BookOpen, route: '/teacher/dashboard/lesson-plans' },
    { id: 'grading', label: 'Grading', icon: GraduationCap, route: '/teacher/dashboard/grading' },
  ];

  ngOnInit(): void {
    this.amplitudeService.trackEvent('dashboard_viewed');
    this.subscriptionService.loadUserProfile();
  }

  // Layout methods
  protected toggleMobileSidebar(): void {
    this.layoutService.toggleMobileSidebar();
  }

  protected closeMobileSidebar(): void {
    this.layoutService.closeMobileSidebar();
  }

  protected toggleUserMenu(): void {
    this.layoutService.toggleUserMenu();
  }

  protected closeUserMenu(): void {
    this.layoutService.closeUserMenu();
  }

  // Subscription methods
  protected onSubscriptionsClick(): void {
    this.subscriptionService.onSubscriptionsClick();
    this.closeUserMenu();
  }

  protected closePricingModal(): void {
    this.subscriptionService.closePricingModal();
  }

  protected onPaymentSuccess(product: { name: string }): void {
    this.subscriptionService.onPaymentSuccess(product as any);
  }

  protected closeSubscriptionSuccess(): void {
    this.subscriptionService.closeSubscriptionSuccess();
  }

  protected closeSubscriptionInfo(): void {
    this.subscriptionService.closeSubscriptionInfo();
  }

  protected onCancelSubscription(): void {
    this.subscriptionService.cancelSubscription();
  }

  protected onCancellationVerificationComplete(): void {
    this.subscriptionService.onCancellationVerificationComplete();
  }

  // Feedback methods
  protected openDashboardFeedback(): void {
    this.feedbackService.openDashboardFeedback();
  }

  protected closeDashboardFeedback(): void {
    this.feedbackService.closeDashboardFeedback();
  }

  protected onDashboardFeedbackSubmit(event: { feedbackText: string; feedbackType: string }): void {
    this.feedbackService.submitFeedback(event.feedbackText, event.feedbackType);
  }

  // Toast methods
  protected hideToast(): void {
    this.toastService.hide();
  }

  // Auth methods
  protected logout(): void {
    this.amplitudeService.trackEvent('logout');
    this.authService
      .logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.router.navigate(['/']);
      });
  }
}
